/* Copyright 2012 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { SCROLLBAR_PADDING, ScrollMode, SpreadMode } from "./ui_utils.js";
import { CursorTool } from "./pdf_cursor_tools.js";
import { PagesCountLimit } from "./base_viewer.js";

class SecondaryToolbar {
  constructor(options, mainContainer, eventBus) {
    this.toolbar = options.toolbar;
    this.toggleButton = options.toggleButton;
    this.toolbarButtonContainer = options.toolbarButtonContainer;

    this.mainContainer = mainContainer;
    this.eventBus = eventBus;

    this.opened = false;
    this.containerHeight = null;
    this.previousContainerHeight = null;
    this.bookmarksList = document.getElementById('customBookmarksList');
    this.bookmarks = {};

    this.notesList = document.getElementById('customNotesList');
    this.notes = {};

    this.reset();

    // Bind the event listeners for click, cursor tool, and scroll/spread mode
    // actions.
    this.#bindClickListeners();

    // Bind the event listener for adjusting the 'max-height' of the toolbar.
    this.eventBus._on("resize", this.#setMaxHeight.bind(this));
    this._updateBookmarksEmptyState()
    this.eventBus.on('documentloaded', (evt) => this._onDocumentLoaded(evt))
  }

  _onDocumentLoaded () {
    if (window.PDFViewerApplication.api.operating()) {
      this.toggleButton.classList.remove('hidden')
    } else {
      this.toggleButton.classList.add('hidden')
    }
  }

  /**
   * @type {boolean}
   */
  get isOpen() {
    return this.opened;
  }

  setPageNumber(pageNumber) {
    this.pageNumber = pageNumber;
    this.#updateUIState();
  }

  setPagesCount(pagesCount) {
    this.pagesCount = pagesCount;
    this.#updateUIState();
  }

  addNote (handle, title) {
    const el = document.createElement('li');
    el.setAttribute('data-handle', handle);
    el.innerHTML = `<a>${title}</a><button></button>`;

    el.addEventListener('click', (e) => {
      if (e.target.nodeName.toLowerCase() === 'button') {
        this.eventBus.dispatch('deleteNote', {handle})
      } else {
        this.eventBus.dispatch('openNote', {handle});
      }
    })

    this.notesList.appendChild(el);
    this.notes[handle] = {title, el};
    this._updateNotesEmptyState()
  }

  clearNotes () {
    this.notesList.innerHTML = ''
    this.notes = {}
    this._updateNotesEmptyState()
  }

  addBookmark(handle, title) {
    const el = document.createElement('li');
    el.setAttribute('data-handle', handle);
    el.innerHTML = `<a>${title}</a><button></button>`;

    el.addEventListener('click', (e) => {
      if (e.target.nodeName.toLowerCase() === 'button') {
        this.eventBus.dispatch('deleteBookmark', {handle});
      } else {
        this.eventBus.dispatch('openBookmark', {handle});
      }
    })


    this.bookmarksList.appendChild(el);
    this.bookmarks[handle] = {title, el};
    this._updateBookmarksEmptyState()
  }

  deleteBookmark(handle) {
    this.bookmarks[handle].el.remove()
    delete this.bookmarks[handle]
    this._updateBookmarksEmptyState()
  }

  reset() {
    this.pageNumber = 0;
    this.pagesCount = 0;
    this.#updateUIState();

    // Reset the Scroll/Spread buttons too, since they're document specific.
    this.eventBus.dispatch("secondarytoolbarreset", { source: this });
  }

  _updateBookmarksEmptyState() {
    if (Object.keys(this.bookmarks).length) {
      document.getElementById('custom_bookmarks_empty').classList.add('hidden')
    } else {
      document.getElementById('custom_bookmarks_empty').classList.remove('hidden')
    }
  }

  _updateNotesEmptyState() {
    if (Object.keys(this.notes).length) {
      document.getElementById('custom_notes_empty').classList.add('hidden')
    } else {
      document.getElementById('custom_notes_empty').classList.remove('hidden')
    }
  }

  #updateUIState() {
  }

  #bindClickListeners() {
    this.toggleButton.addEventListener("click", this.toggle.bind(this));
  }

  open() {
    if (this.opened) {
      return;
    }
    this.opened = true;
    this.#setMaxHeight();

    this.toggleButton.classList.add("toggled");
    this.toggleButton.setAttribute("aria-expanded", "true");
    this.toolbar.classList.remove("hidden");
  }

  close() {
    if (!this.opened) {
      return;
    }
    this.opened = false;
    this.toolbar.classList.add("hidden");
    this.toggleButton.classList.remove("toggled");
    this.toggleButton.setAttribute("aria-expanded", "false");
  }

  toggle() {
    if (this.opened) {
      this.close();
    } else {
      this.open();
    }
  }

  #setMaxHeight() {
    if (!this.opened) {
      return; // Only adjust the 'max-height' if the toolbar is visible.
    }
    this.containerHeight = this.mainContainer.clientHeight;

    if (this.containerHeight === this.previousContainerHeight) {
      return;
    }
    this.toolbarButtonContainer.style.maxHeight = `${
      this.containerHeight - SCROLLBAR_PADDING
    }px`;

    this.previousContainerHeight = this.containerHeight;
  }
}

export { SecondaryToolbar };
